import React, {useCallback, useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {CloseAction, ContextEditor} from '../ContextEditor';
import {ContextList} from '../ContextList';
import {Filter} from '../Filter';

import {useAppSelector} from '../../app/hooks';
import {translationSelector} from '../../slices/translation';
import {setCloseProtection} from '../../utils/closeProtection';

interface ContextsProps {
  locale: string
  onDontHide: (donthide: boolean) => void
}

export const Contexts = ({locale, onDontHide}: ContextsProps) => {
  const {contexts, filteredList} = useAppSelector(translationSelector)
  const [editContextID, updateEditContextID] = useState<string>()
  const history = useHistory()
  const location = useLocation()

  const setContextParam = useCallback((id?: string) => {
    const params = new URLSearchParams(location.search)
    if (id) params.set('context', id)
    else params.delete('context')
    const search = params.toString()
    history.replace({ pathname: location.pathname, search: search ? `?${search}` : '' })
  }, [history, location.pathname, location.search])

  const onEditContext = useCallback(
    (contextID: string | undefined) => {
      updateEditContextID(contextID)
      onDontHide(!!contextID)
      setContextParam(contextID)
    },
    [onDontHide, setContextParam]
  )

  const onClose = useCallback(
    (action: CloseAction, filteredList: string[], oldContextID: string) => {
      const index = filteredList.findIndex(id => id === oldContextID)
      let contextID;

      switch (action) {
        case 'previous':
          if (index > 0)
            contextID = filteredList[index - 1]
          break

        case 'next':
          if (index < filteredList.length - 1)
            contextID = filteredList[index + 1]
          break

        default:
          break
      }

      if (!contextID) setCloseProtection(false)
      onEditContext(contextID)
    },
    [onEditContext]
  )

  // Restore editor state from URL on load/refresh
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const id = params.get('context') || undefined
    if (!id) return
    // only open when contexts are available and contain the id
    if (contexts[id]) {
      updateEditContextID(id)
      onDontHide(true)
    }
  }, [contexts, location.search, onDontHide])

  if (editContextID)
    return (
      <ContextEditor
        entries={Object.values(contexts[editContextID].translations)}
        locale={locale}
        onClose={action => onClose(action, filteredList, editContextID)}
      />
    )

  return (
    <>
      <Filter />
      <ContextList onEditContext={onEditContext} />
    </>
  )
}
